import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import VerifiedIcon from '@mui/icons-material/Verified';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ModernDateRangePicker } from '../../components/ModernDateRangePicker';
import { CarteiraSelector } from '../../components/CarteiraSelector/CarteiraSelector';
import SearchBar from '../../components/SearchBar/SearchBar';
import { fetchNotifications, NotificationItem } from '../../services/notificationAPI';
import { useNotificationToasts } from '../../hooks/useNotificationToasts';
import { NotificationToastContainer } from '../NotificationToast/NotificationToast'
import styles from './ModernDashboardHeader.module.css';

interface ModernDashboardHeaderProps {
    onClientSelect?: (client: { name: string; id: number }) => void;
    selectedCarteira?: string;
    title?: string;
    subtitle?: string;
    actions?: ReactNode;
    children?: ReactNode;
    variant?: 'default' | 'minimal';
    cacheInfo?: {
        cached?: boolean;
        cachedAt?: string;
        onRefresh: () => Promise<void>;
        isRefreshing: boolean;
    };
}

const ModernDashboardHeader: React.FC<ModernDashboardHeaderProps> = ({
    onClientSelect,
    selectedCarteira,
    title,
    subtitle,
    actions,
    children,
    variant = 'default',
    cacheInfo
}) => {
    const navigate = useNavigate();
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const notificationRef = useRef<HTMLDivElement>(null);
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [loadingNotes, setLoadingNotes] = useState(false);
    
    // Cooldown para o botão de atualizar dados
    const [cacheCooldown, setCacheCooldown] = useState(0);

    useEffect(() => {
        if (cacheCooldown <= 0) return;
        const timer = setTimeout(() => setCacheCooldown(cacheCooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [cacheCooldown]);

    const handleCacheRefreshClick = async () => {
        if (!cacheInfo || cacheInfo.isRefreshing || cacheCooldown > 0) return;
        try {
            await cacheInfo.onRefresh();
            setCacheCooldown(15); // 15 segundos de cooldown
        } catch (err) {
            console.error('Falha ao forçar atualização via cabeçalho:', err);
        }
    };

    const formatCacheTime = (isoString?: string) => {
        if (!isoString) return '';
        try {
            const date = new Date(isoString);
            return date.toLocaleTimeString('pt-BR');
        } catch {
            return '';
        }
    };

    // 1. CORREÇÃO: Inicializa dismissedIds com LocalStorage para persistir após F5
    const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
        const saved = localStorage.getItem('dismissedNotificationIds');
        return saved ? JSON.parse(saved) : [];
    });

    // Estado para Notificações Lidas (Salvo no LocalStorage + Cookies)
    const [readIds, setReadIds] = useState<string[]>(() => {
        try {
            const saved = localStorage.getItem('readNotificationIds');
            if (saved) return JSON.parse(saved);
        } catch (e) { }
        const cookieMatch = document.cookie.match(/(?:^|; )readNotificationIds=([^;]*)/);
        if (cookieMatch) {
            try { return JSON.parse(decodeURIComponent(cookieMatch[1])); } catch (e) { }
        }
        return [];
    });

    const { toasts, removeToast, checkForNewNotifications, addToast } = useNotificationToasts();

    // Salva no LocalStorage sempre que um item for removido
    useEffect(() => {
        localStorage.setItem('dismissedNotificationIds', JSON.stringify(dismissedIds));
    }, [dismissedIds]);

    // Salva o status de lido no LocalStorage e nos Cookies
    useEffect(() => {
        try {
            localStorage.setItem('readNotificationIds', JSON.stringify(readIds));
            document.cookie = `readNotificationIds=${encodeURIComponent(JSON.stringify(readIds))}; max-age=${30 * 24 * 60 * 60}; path=/; SameSite=Lax`;
        } catch (e) {
            console.error('Erro ao armazenar estado de leitura das notificações:', e);
        }
    }, [readIds]);

    useEffect(() => {
        const loadNotifications = async () => {
            try {
                setLoadingNotes(true);
                const response = await fetchNotifications();
                setNotifications(response.data);
                checkForNewNotifications(response.data);
            } catch (error) {
                console.error("Erro ao carregar notificações", error);
            } finally {
                setLoadingNotes(false);
            }
        };
        loadNotifications();
        const interval = setInterval(loadNotifications, 300000);
        return () => clearInterval(interval);
    }, [selectedCarteira, checkForNewNotifications]);

    // Limpeza automática de IDs salvos nos cookies/localStorage que já sumiram do backend
    useEffect(() => {
        if (notifications.length > 0) {
            const activeIds = new Set(notifications.map(n => n.id));
            setReadIds(prev => {
                const cleaned = prev.filter(id => activeIds.has(id));
                return cleaned.length !== prev.length ? cleaned : prev;
            });
            setDismissedIds(prev => {
                const cleaned = prev.filter(id => activeIds.has(id));
                return cleaned.length !== prev.length ? cleaned : prev;
            });
        }
    }, [notifications]);

    // Lógica para lembretes de QC (2x por dia)
    useEffect(() => {
        const checkQCReminders = () => {
            const pendingQc = notifications.filter(
                n => n.type === 'qc_autorizado' && !dismissedIds.includes(n.id)
            );

            if (pendingQc.length === 0) return;

            const now = new Date();
            const lastCheckKey = 'lastQcReminderCheck';
            const lastCheckTimestampStr = localStorage.getItem(lastCheckKey);
            const lastCheckTimestamp = lastCheckTimestampStr ? parseInt(lastCheckTimestampStr, 10) : 0;

            const twelveHours = 12 * 60 * 60 * 1000;

            if (now.getTime() - lastCheckTimestamp >= twelveHours) {
                addToast({
                    id: `qc-reminder-${Date.now()}`,
                    title: 'Ação Necessária: Reclamação Autorizada',
                    description: `Você tem ${pendingQc.length} nova(s) autorização(ões) de CQ aguardando. Por favor, vincule um pedido de reposição para concluir o processo.`,
                    type: 'qc_autorizado',
                    time: 'Urgente',
                    read: false
                });
                localStorage.setItem(lastCheckKey, now.getTime().toString());
            }
        };

        checkQCReminders();
    }, [notifications, dismissedIds, addToast]);

    const dropdownNotifications = notifications.filter(
        n => !dismissedIds.includes(n.id)
    );

    const handleDismiss = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setDismissedIds(prev => [...prev, id]);
    };

    const markAsRead = (id: string) => {
        if (!readIds.includes(id)) {
            setReadIds(prev => [...prev, id]);
        }
    };

    const markAllAsRead = () => {
        const allIds = dropdownNotifications.map(n => n.id);
        setReadIds(prev => Array.from(new Set([...prev, ...allIds])));
    };

    // Apenas as notificações NÃO lidas entram na contagem do badge
    const unreadCount = dropdownNotifications.filter(n => !readIds.includes(n.id)).length;

    const getItemClass = (type: string) => {
        switch (type) {
            case 'alert': return styles.itemAlert;
            case 'qc_autorizado': return styles.itemAlert;
            case 'success': return styles.itemSuccess;
            case 'early_success': return styles.itemEarly;
            default: return styles.itemDefault;
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
                setIsNotificationOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'sale': return <TrendingUpIcon sx={{ fontSize: 18 }} />;
            case 'alert': return <WarningAmberIcon sx={{ fontSize: 18 }} style={{ color: '#ef4444' }} />;
            case 'qc_autorizado': return <VerifiedIcon sx={{ fontSize: 18 }} style={{ color: '#d97706' }} />;
            case 'early_success': return <AccessTimeIcon sx={{ fontSize: 18 }} style={{ color: '#16a34a' }} />;
            case 'success': return <CheckCircleOutlineIcon sx={{ fontSize: 18 }} style={{ color: '#16a34a' }} />;
            default: return <NotificationsIcon sx={{ fontSize: 18 }} />;
        }
    };

    const handleNotificationClick = (notification: NotificationItem) => {
        markAsRead(notification.id);
        setIsNotificationOpen(false);
        if (notification.type === 'qc_autorizado' && notification.metadata?.reclamacaoId) {
            navigate(`/reclamacoes?id=${notification.metadata.reclamacaoId}`);
        } else if (notification.type === 'qc_autorizado') {
            navigate(`/reclamacoes`);
        } else if (notification.metadata?.orderId) {
            navigate(`/pedidos?orderId=${notification.metadata.orderId}`);
        }
    };

    const notificationBell = (
        <div className={styles.notificationWrapper} ref={notificationRef}>
            <button
                className={`${styles.notificationButton} ${isNotificationOpen ? styles.buttonActive : ''} ${unreadCount > 0 ? styles.buttonHasUnread : ''}`}
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                title="Notificações"
            >
                {unreadCount > 0 ? (
                    <NotificationsIcon className={styles.bellIconActive} sx={{ fontSize: 22 }} />
                ) : (
                    <NotificationsOutlinedIcon className={styles.bellIconInactive} sx={{ fontSize: 22 }} />
                )}
                {unreadCount > 0 && <span className={styles.notificationBadge}>{unreadCount}</span>}
            </button>

            {isNotificationOpen && (
                <div className={styles.notificationDropdown}>
                    <div className={styles.notificationHeader}>
                        <h3>Notificações</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {unreadCount > 0 && (
                                <button
                                    className={styles.markAllReadBtn}
                                    onClick={markAllAsRead}
                                    title="Marcar todas como lidas"
                                >
                                    Marcar lidas
                                </button>
                            )}
                            {unreadCount > 0 ? (
                                <span className={styles.unreadText}>{unreadCount} novas</span>
                            ) : (
                                <span className={styles.allReadText}>Todas lidas</span>
                            )}
                        </div>
                    </div>

                    <div className={styles.notificationList}>
                        {loadingNotes ? (
                            <div className={styles.emptyNotifications}><p>Carregando...</p></div>
                        ) : dropdownNotifications.length === 0 ? (
                            <div className={styles.emptyNotifications}>
                                <NotificationsNoneIcon sx={{ fontSize: 40 }} />
                                <p>Nenhuma notificação</p>
                            </div>
                        ) : (
                            dropdownNotifications.slice(0, 15).map(notification => {
                                const isRead = readIds.includes(notification.id);
                                return (
                                    <div
                                        key={notification.id}
                                        className={`${styles.notificationItem} ${getItemClass(notification.type)} ${isRead ? styles.itemRead : styles.itemUnread}`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <button
                                            className={styles.dismissButton}
                                            onClick={(e) => handleDismiss(e, notification.id)}
                                            title="Remover desta lista"
                                        >
                                            <CloseIcon sx={{ fontSize: 14 }} />
                                        </button>

                                        <div className={`${styles.notificationIcon} ${isRead ? styles.iconRead : ''}`}>
                                            {getNotificationIcon(notification.type)}
                                        </div>
                                        <div className={styles.notificationContent}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '16px' }}>
                                                <h4 className={isRead ? styles.titleRead : ''}>{notification.title}</h4>
                                                {!isRead && <span className={styles.unreadDot} title="Não lida"></span>}
                                            </div>
                                            <p className={isRead ? styles.textRead : ''}>{notification.description}</p>
                                            <span className={styles.notificationTime}>{notification.time}</span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <>
            {variant === 'minimal' ? (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {cacheInfo && (
                            <div className={styles.cacheContainer}>
                                <div className={`${styles.cacheStatusLabel} ${cacheInfo.cached ? styles.cacheCached : styles.cacheRealtime}`}>
                                    <span className={styles.cacheIndicatorDot}></span>
                                    <span>Atualizado às {formatCacheTime(cacheInfo.cachedAt) || '--:--:--'}</span>
                                </div>
                                <button 
                                    onClick={handleCacheRefreshClick}
                                    disabled={cacheInfo.isRefreshing || cacheCooldown > 0}
                                    className={styles.cacheRefreshIconButton}
                                    title={cacheCooldown > 0 ? `Aguarde ${cacheCooldown}s` : "Atualizar dados"}
                                >
                                    {cacheInfo.isRefreshing ? (
                                        <span className={styles.cacheSpinner}></span>
                                    ) : cacheCooldown > 0 ? (
                                        <span className={styles.cacheCooldownText}>{cacheCooldown}s</span>
                                    ) : (
                                        <RefreshIcon sx={{ fontSize: 16 }} />
                                    )}
                                </button>
                            </div>
                        )}
                        {notificationBell}
                    </div>
                </>
            ) : (
                <div className={styles.headerContainer}>
                    {(title || subtitle) && (
                        <div className={styles.topBar}>
                            <div className={styles.titleSection}>
                                {title && <h1 className={styles.pageTitle}>{title}</h1>}
                                {subtitle && <p className={styles.pageSubtitle}>{subtitle}</p>}
                            </div>
                        </div>
                    )}
                    
                    <div className={styles.filterBar}>
                        <div className={styles.filterGroup}>
                            {children ? (
                                children
                            ) : (
                                <>
                                    <div className={styles.filterItem}><ModernDateRangePicker /></div>
                                    <div className={styles.filterItem}><CarteiraSelector /></div>
                                    {onClientSelect && (
                                        <div className={styles.filterItem}>
                                            <SearchBar onClientSelect={onClientSelect} selectedCarteira={selectedCarteira || ''} />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {actions && <div className={styles.centerSection}>{actions}</div>}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {cacheInfo && (
                                <div className={styles.cacheContainer}>
                                    <div className={`${styles.cacheStatusLabel} ${cacheInfo.cached ? styles.cacheCached : styles.cacheRealtime}`}>
                                        <span className={styles.cacheIndicatorDot}></span>
                                        <span>Atualizado às {formatCacheTime(cacheInfo.cachedAt) || '--:--:--'}</span>
                                    </div>
                                    <button 
                                        onClick={handleCacheRefreshClick}
                                        disabled={cacheInfo.isRefreshing || cacheCooldown > 0}
                                        className={styles.cacheRefreshIconButton}
                                        title={cacheCooldown > 0 ? `Aguarde ${cacheCooldown}s` : "Atualizar dados"}
                                    >
                                        {cacheInfo.isRefreshing ? (
                                            <span className={styles.cacheSpinner}></span>
                                        ) : cacheCooldown > 0 ? (
                                            <span className={styles.cacheCooldownText}>{cacheCooldown}s</span>
                                        ) : (
                                            <RefreshIcon sx={{ fontSize: 16 }} />
                                        )}
                                    </button>
                                </div>
                            )}
                            {notificationBell}
                        </div>
                    </div>
                </div>
            )}

            <NotificationToastContainer
                toasts={toasts}
                onClose={removeToast}
                onClick={handleNotificationClick}
            />
        </>
    );
};

export default ModernDashboardHeader;